import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFreeTrialContext } from "../../contexts/FreeTrialContext";
import { PROGRAMS } from "../SkillsAcademy/data";
import { Icons } from "../SkillsAcademy/icons.jsx";
import TopicSection from "./TopicSection";
import UnlockModal from "../UnlockModal/UnlockModal";
import ActionButton from "../ActionButton/ActionButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  ProgramDetailRoot,
  ProgramDetailBackBtn,
  ProgramDetailHero,
  ProgramDetailPosterWrap,
  ProgramDetailPosterImg,
  ProgramDetailPosterTint,
  ProgramDetailPosterContent,
  ProgramDetailPillRow,
  ProgramDetailHeroTitle,
  ProgramDetailGuruRow,
  ProgramDetailGuruAvatar,
  ProgramDetailGuruText,
  ProgramDetailGuruName,
  ProgramDetailSidebar,
  ProgramDetailOverviewLabel,
  ProgramDetailBlurb,
  ProgramDetailStatsGrid,
  ProgramDetailStatItem,
  ProgramDetailStatIconRow,
  ProgramDetailStatLabel,
  ProgramDetailStatNum,
  ProgramDetailStatusBanner,
  ProgramDetailStatusMain,
  ProgramDetailStatusSub,
  ProgramDetailProgressSection,
  ProgramDetailProgressLabel,
  ProgramDetailProgressHeading,
  ProgramDetailProgressPct,
  ProgramDetailProgressTrack,
  ProgramDetailProgressFill,
  ProgramDetailProgressSessionsRow,
  ProgramDetailProgressSessionChip,
  ProgramDetailActions,
  ProgramDetailBtnSecondary,
  ProgramDetailTopicsSection,
  ProgramDetailTopicsHeader,
  ProgramDetailTopicsTitle,
  ProgramDetailTopicsList,
  ProgramDetailUpgradeStrip,
  ProgramDetailUpgradeText,
  ProgramDetailUpgradeTitle,
  ProgramDetailUpgradeSubtitle,
  DetailPill,
} from "./style.js";
import { AccordionRoot } from "../SkillsAcademy/style.js";

// ── Component ─────────────────────────────────────────────────────────────────

const ProgramDetail = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { enrollments } = useFreeTrialContext();
  const [openStates, setOpenStates] = useState<boolean[]>(() => {
    const p = PROGRAMS.find((p) => p.id === programId);
    return (p?.topics ?? []).map((_, i) => i === 0);
  });
  const [unlockOpen, setUnlockOpen] = useState(false);

  const trialEnrollment = enrollments.find((e) => e.programId === programId);
  const program = PROGRAMS.find((p) => p.id === programId);

  const allExpanded = openStates.length > 0 && openStates.every(Boolean);
  const handleToggleAll = () =>
    setOpenStates((prev) => prev.map(() => !allExpanded));
  const handleToggleOne = (i: number) =>
    setOpenStates((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  if (!program) {
    return (
      <ProgramDetailRoot>
        <ProgramDetailBackBtn type="button" onClick={() => navigate("/dashboard/my-programs")}>
          <Icons.arrowLeft size={16} />
          Back to My Programs
        </ProgramDetailBackBtn>
        <div style={{ padding: "60px 40px", textAlign: "center", color: "#D1C7FF" }}>
          Program not found.
        </div>
      </ProgramDetailRoot>
    );
  }

  const isTrial = !!trialEnrollment;
  const enrollment = trialEnrollment ?? null;

  const overallProgress = enrollment?.overallProgress ?? 0;
  const sessionsCompleted = enrollment?.sessionsCompleted ?? 0;
  const sessionsTotal = enrollment?.sessionsTotal ?? program.lessons;
  const simsCompleted = enrollment?.simulationsCompleted ?? 0;
  const simsTotal = enrollment?.simulationsTotal ?? program.sims;
  const completed = enrollment?.completed ?? false;
  const daysLeftInTrial = trialEnrollment?.daysLeftInTrial ?? 10;
  const accent = (program.accent as string[]) ?? ["#01ECFF", "#962EFF"];

  const statusType = completed ? "completed" : isTrial ? "trial" : "active";

  const topicStatuses = (program.topics ?? []).map((_, i) => {
    if (isTrial) return i === 0 ? ("in-progress" as const) : ("locked" as const);
    if (completed) return "completed" as const;
    if (i === 0) return "in-progress" as const;
    return "up-next" as const;
  });

  // ── Per-session visual states ──────────────────────────────────────────────
  type SessionStatus = "completed" | "next" | "available" | "locked";

  const sessionStatesByTopic: SessionStatus[][] = (() => {
    const topics = program.topics ?? [];

    if (isTrial) {
      // Count how many freeTrial skills exist so we know when to unlock sims
      const freeSkillCount = (topics[0]?.sessions ?? []).filter(
        (s: { type: string; freeTrial?: boolean }) => s.type === "skill" && s.freeTrial
      ).length;
      const allFreeSkillsDone = sessionsCompleted >= freeSkillCount;

      let skillsLeft = sessionsCompleted;
      let simsLeft = simsCompleted;
      let skillNextAssigned = false;
      let simNextAssigned = false;

      return topics.map((topic, ti) =>
        topic.sessions.map((s: { type: string; freeTrial?: boolean }) => {
          // Non-trial topics or non-freeTrial sessions are always locked
          if (ti !== 0 || !s.freeTrial) return "locked";

          if (s.type === "skill") {
            if (skillsLeft > 0) { skillsLeft--; return "completed"; }
            if (!skillNextAssigned) { skillNextAssigned = true; return "next"; }
            return "available";
          } else {
            // Sims locked until all free skills are completed
            if (!allFreeSkillsDone) return "locked";
            if (simsLeft > 0) { simsLeft--; return "completed"; }
            if (!simNextAssigned) { simNextAssigned = true; return "next"; }
            return "available";
          }
        })
      );
    }

    if (completed) {
      return topics.map((topic) => topic.sessions.map(() => "completed" as const));
    }

    // Paid in-progress: allocate sequential completions across all sessions
    let skillsLeft = sessionsCompleted;
    let simsLeft = simsCompleted;
    let skillNextFound = false;
    let simNextFound = false;

    return topics.map((topic) =>
      topic.sessions.map((s: { type: "skill" | "sim" }) => {
        if (s.type === "skill") {
          if (skillsLeft > 0) { skillsLeft--; return "completed"; }
          if (!skillNextFound) { skillNextFound = true; return "next"; }
          return "available";
        } else {
          if (simsLeft > 0) { simsLeft--; return "completed"; }
          if (!simNextFound) { simNextFound = true; return "next"; }
          return "available";
        }
      })
    );
  })();

  return (
    <ProgramDetailRoot>
      <ProgramDetailBackBtn type="button" onClick={() => navigate("/dashboard/my-programs")}>
        <Icons.arrowLeft size={16} />
        Back to My Programs
      </ProgramDetailBackBtn>

      {/* ── Hero ── */}
      <ProgramDetailHero>
        {/* Poster — rounded frame */}
        <ProgramDetailPosterWrap>
          <ProgramDetailPosterImg $src={program.poster} />
          <ProgramDetailPosterTint $a={accent[0]} $b={accent[1]} />
          <ProgramDetailPosterContent>
            <ProgramDetailPillRow>
              {isTrial && (
                <DetailPill
                  $bg="linear-gradient(95deg, rgba(1,236,255,0.85), rgba(150,46,255,0.85))"
                  $fg="#030039"
                  $bd="transparent"
                >
                  <Icons.zap size={10} /> Free Trial
                </DetailPill>
              )}
              {program.category && (
                <DetailPill
                  $bg="rgba(0,0,0,0.55)"
                  $fg="#D1C7FF"
                  $bd="rgba(255,255,255,0.18)"
                >
                  {program.category}
                </DetailPill>
              )}
            </ProgramDetailPillRow>
            <ProgramDetailHeroTitle>{program.title}</ProgramDetailHeroTitle>
            {program.guru && (
              <ProgramDetailGuruRow>
                <ProgramDetailGuruAvatar $src={program.guru.avatar} />
                <ProgramDetailGuruText>
                  A Program By{" "}
                  <ProgramDetailGuruName>{program.guru.name}</ProgramDetailGuruName>
                  {program.guru.role && ` · ${program.guru.role}`}
                </ProgramDetailGuruText>
              </ProgramDetailGuruRow>
            )}
          </ProgramDetailPosterContent>
        </ProgramDetailPosterWrap>

        {/* Sidebar */}
        <ProgramDetailSidebar>
          <div>
            <ProgramDetailOverviewLabel>Program Overview</ProgramDetailOverviewLabel>
            <ProgramDetailBlurb>{program.blurb}</ProgramDetailBlurb>
          </div>

          {/* Stats — 4 individual boxes */}
          <ProgramDetailStatsGrid>
            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.flame size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Difficulty Level</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{program.level ?? "—"}</ProgramDetailStatNum>
            </ProgramDetailStatItem>

            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.sparkle size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Topics</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{(program.topics ?? []).length}</ProgramDetailStatNum>
            </ProgramDetailStatItem>

            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.book size={12} style={{ color: "#01ECFF" }} />
                <ProgramDetailStatLabel>Skill Dev Sessions</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{program.lessons}</ProgramDetailStatNum>
            </ProgramDetailStatItem>

            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.users size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Simulations</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{program.sims}</ProgramDetailStatNum>
            </ProgramDetailStatItem>
          </ProgramDetailStatsGrid>

          {/* Status banner */}
          <ProgramDetailStatusBanner $type={statusType}>
            {isTrial && (
              <Icons.zap size={20} style={{ color: "#01ECFF", flexShrink: 0 }} />
            )}
            {completed && (
              <Icons.check size={20} style={{ color: "#17B26A", flexShrink: 0 }} />
            )}
            <div>
              <ProgramDetailStatusMain $type={statusType}>
                {completed
                  ? "Program completed"
                  : isTrial
                    ? "Free trial active"
                    : "Enrolled"}
              </ProgramDetailStatusMain>
              {isTrial && !completed && (
                <ProgramDetailStatusSub>· {daysLeftInTrial} days remaining</ProgramDetailStatusSub>
              )}
            </div>
          </ProgramDetailStatusBanner>

          {/* Progress tile */}
          <ProgramDetailProgressSection>
            <ProgramDetailProgressLabel>
              <ProgramDetailProgressHeading>Program Progress</ProgramDetailProgressHeading>
              <ProgramDetailProgressPct $isCompleted={completed}>
                {overallProgress}% completed
              </ProgramDetailProgressPct>
            </ProgramDetailProgressLabel>
            <ProgramDetailProgressTrack>
              <ProgramDetailProgressFill $pct={overallProgress} $isCompleted={completed} />
            </ProgramDetailProgressTrack>
            <ProgramDetailProgressSessionsRow>
              <ProgramDetailProgressSessionChip>
                <Icons.book size={12} style={{ color: "#01ECFF" }} />
                {sessionsCompleted} / {sessionsTotal} Skill Sessions
              </ProgramDetailProgressSessionChip>
              <ProgramDetailProgressSessionChip>
                <Icons.users size={12} style={{ color: "#A87EFF" }} />
                {simsCompleted} / {simsTotal} Simulations
              </ProgramDetailProgressSessionChip>
            </ProgramDetailProgressSessionsRow>
          </ProgramDetailProgressSection>

          {/* Actions */}
          <ProgramDetailActions>
            {completed ? (
              <ProgramDetailBtnSecondary type="button">
                <Icons.award size={16} />
                View certificate
              </ProgramDetailBtnSecondary>
            ) : (
              <ActionButton
                variant="primary"
                size="md"
                fullWidth
                icon={<Icons.play size={14} />}
                type="button"
              >
                {overallProgress === 0 ? "Start session" : "Continue"}
              </ActionButton>
            )}
            {isTrial && !completed && (
              <ActionButton
                variant="subtle"
                size="md"
                fullWidth
                icon={<Icons.zap size={14} />}
                type="button"
                onClick={() => setUnlockOpen(true)}
              >
                Unlock full program
              </ActionButton>
            )}
          </ProgramDetailActions>
        </ProgramDetailSidebar>
      </ProgramDetailHero>

      {/* ── Topics ── */}
      <ProgramDetailTopicsSection>
        <ProgramDetailTopicsHeader>
          <ProgramDetailTopicsTitle>Program Topics</ProgramDetailTopicsTitle>
          <ActionButton
            variant="subtle"
            size="sm"
            type="button"
            icon={
              allExpanded ? (
                <RemoveIcon sx={{ fontSize: 14 }} />
              ) : (
                <AddIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={handleToggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </ActionButton>
        </ProgramDetailTopicsHeader>
        <ProgramDetailTopicsList>
          <AccordionRoot>
            {(program.topics ?? []).map((topic, i) => (
              <TopicSection
                key={i}
                topic={topic}
                status={topicStatuses[i]}
                isOpen={openStates[i] ?? false}
                onToggle={() => handleToggleOne(i)}
                isTrial={isTrial}
                topicIndex={i}
                sessionStatuses={sessionStatesByTopic[i]}
              />
            ))}
          </AccordionRoot>
        </ProgramDetailTopicsList>
      </ProgramDetailTopicsSection>

      {/* ── Upgrade strip — trial only ── */}
      {isTrial && !completed && (
        <ProgramDetailUpgradeStrip>
          <ProgramDetailUpgradeText>
            <ProgramDetailUpgradeTitle>Loving it so far?</ProgramDetailUpgradeTitle>
            <ProgramDetailUpgradeSubtitle>
              Unlock the full program to access all {(program.topics ?? []).length} topics,{" "}
              {program.lessons} sessions and {program.sims} simulations.
            </ProgramDetailUpgradeSubtitle>
          </ProgramDetailUpgradeText>
          <ActionButton
            variant="primary"
            size="lg"
            icon={<Icons.zap size={18} />}
            type="button"
            onClick={() => setUnlockOpen(true)}
          >
            Unlock full program
          </ActionButton>
        </ProgramDetailUpgradeStrip>
      )}

      <UnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} />
    </ProgramDetailRoot>
  );
};

export default ProgramDetail;
